import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../Core/Context/AuthContext';
import {
  ACCESS_TOKEN,
  REFRESH_TOKEN,
  TOKEN_EXP,
  ID_TOKEN,
} from '../../../constants/applicationConstants';

export default function LoginCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hydrateCurrentUser, setAuthTokens } = useAuth();

  useEffect(() => {
    const handleAuth = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const redirectTo = searchParams.get('redirectTo') || '/';
      const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
      const storedState = sessionStorage.getItem('pkce_state');

      if (!code) {
        console.error('No authorization code found.');
        navigate('/login');
        return;
      }

      if (state && storedState && state !== storedState) {
        console.warn(
          'State does not match stored state. Continuing with caution.'
        );
      }

      try {
        const tokenUrl = process.env.OIDC_TOKEN_URL || '';
        const clientId = process.env.OIDC_CLIENT_ID || '';
        const clientSecret = process.env.OIDC_CLIENT_SECRET || '';

        // Prefer configured redirect URI, otherwise derive from current window location
        const redirectUri =
          process.env.OIDC_REDIRECT_URI ||
          `${window.location.origin}${window.location.pathname}`;

        if (!code) throw new Error('Missing authorization code');
        if (!redirectUri) throw new Error('Missing redirect_uri');
        if (!clientId) throw new Error('Missing client_id');
        if (!codeVerifier) throw new Error('Missing code_verifier');
        if (!clientSecret) throw new Error('Missing client_secret');
        if (!state) throw new Error('Missing state');
        const body = new URLSearchParams({
          grant_type: 'authorization_code',
          code,
          redirect_uri: redirectUri,
          code_verifier: codeVerifier,
          client_id: clientId,
          client_secret: clientSecret,
          state: state,
        });


        const resp = await fetch(tokenUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          credentials: 'include',
          body: body.toString(),
        });

        if (!resp.ok) {
          console.error('Token request failed', resp);
          navigate('/login?error=loading-timeout');
          return;
        }

        const data = await resp.json();

        // Use context helper to persist tokens and update user (centralized)
        try {
          setAuthTokens(data);
        } catch (err) {
          // fallback to direct storage if helper not available
          const now = Math.floor(Date.now() / 1000);
          if (data.access_token)
            sessionStorage.setItem(ACCESS_TOKEN, data.access_token);
          if (data.refresh_token)
            sessionStorage.setItem(REFRESH_TOKEN, data.refresh_token);
          if (data.id_token) sessionStorage.setItem(ID_TOKEN, data.id_token);
          if (data.expires_in)
            sessionStorage.setItem(
              TOKEN_EXP,
              String(now + Number(data.expires_in))
            );
        }

        await hydrateCurrentUser();

        // Clean up PKCE storage
        sessionStorage.removeItem('pkce_code_verifier');
        sessionStorage.removeItem('pkce_state');

        // Redirect into app
        window.location.replace(redirectTo);
      } catch (err) {
        console.error('Error exchanging code for token', err);
      }
    };

    handleAuth();
  }, [hydrateCurrentUser, navigate, searchParams]);

  return <div>Redirecting...</div>;
}
